import type { FeatureFlagDefinitionEntryInput } from './feature-flags.constants';
import {
  AUTHENTICATION_DISCOVERER_ENABLED_FLAG,
  AUTHENTICATION_DISCOVERER_WIRING_ENABLED_FLAG,
  AUTO_DISABLE_CREDENTIAL_AFTER_AUTH_FAILURES_ENABLED_FLAG,
  SHOULD_USE_CLAUDE_FOR_AUTH_DISCOVERY_FLAG,
  CREDENTIAL_STORES_ENABLED_FLAG,
  CREDENTIALS_STORE_FILESYSTEM_ENABLED_FLAG,
  AUTHENTICATION_COOLDOWN_ENABLED_FLAG,
  NEO_PYTHON_TOOL_ENABLED_FLAG,
  NEO_ATTACK_LEARNINGS_ANALYZER_ENABLED_FLAG,
  NEO_ASK_CODE_BASE_WIDE_SEARCH_ENABLED_FLAG,
  ASK_CODE_BASE_TOOL_ENABLED_FLAG,
  AGGREGATE_HTTP_AUTH_COOKIES_ENABLED_FLAG,
  AUTHENTICATOR_HTTP_ONLY_ENABLED_FLAG,
  AUTH_PROBE_ENABLED_FLAG,
  PROBE_HTTP_CREDENTIAL_AUTO_APPLY_DISABLED_FLAG,
  ARCHIVED_ASSETS_LIFECYCLE_ENABLED_FLAG,
  UNVERIFIED_ASSETS_ALL_STATUSES_SHOWN_FLAG,
  ASSET_MERGE_ENABLED_FLAG,
  ENDPOINT_HAR_REASSOCIATE,
  FEATURE_ANALYSIS_CREDENTIAL_REMOVAL_ENABLED_FLAG,
  SCHEDULED_ASSET_DEDUP_FLAG,
  POST_BATCH_URL_PARAMETRIZATION_ASSET_DEDUP_FLAG,
  CODE_EXECUTION_KUBERNETES_DRIVER_ENABLED_FLAG,
  ILI_PASSIVE_INTEL_ENABLED_FLAG,
  SCAN_JOB_KUBERNETES_DRIVER_ENABLED_FLAG,
  KATANA_PER_CREDENTIAL_DISPATCH_ENABLED_AT_MS_FLAG,
  KATANA_PER_CREDENTIAL_DISPATCH_ENABLED_AT_DEFAULT_MS,
  BEDROCK_SDK_MAX_ATTEMPTS_FLAG,
  DEFAULT_BEDROCK_SDK_MAX_ATTEMPTS,
  JIRA_CONFIGURATION_PROFILES_UI_ENABLED_FLAG,
  CRAWLER_SKIP_FAILED_VERIFICATION_CREDENTIALS_ENABLED_FLAG,
  CRAWLER_STORED_LOGIN_ENABLED_FLAG,
} from './feature-flags.constants';

export const GENERAL_FEATURE_FLAG_DEFINITIONS = [
  {
    name: 'COOKIES_NAME_REGEX_PATTERN',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'HEADERS_NAME_REGEX_PATTERN',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'CRAWLER_ENABLED_ON_CREATE',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CRAWLER_ENABLED_ON_CREDENTIAL_CHANGE',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CRAWLER_PERIODIC_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: CRAWLER_SKIP_FAILED_VERIFICATION_CREDENTIALS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CRAWLER_STORED_LOGIN_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: KATANA_PER_CREDENTIAL_DISPATCH_ENABLED_AT_MS_FLAG,
    type: 'TIMESTAMP' as const,
    defaultValue: KATANA_PER_CREDENTIAL_DISPATCH_ENABLED_AT_DEFAULT_MS,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'REMEDIATION_PLAN_REFRESH_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'REMEDIATION_PR_INTEGRATION_LINKING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'RUN_KB_MIGRATION',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'MISBATCHED_GRAPHQL_HAR_REINGEST',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'GATEWAY_RATE_LIMIT_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'GATEWAY_COPILOT_STREAM_DIAGNOSTICS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: AUTHENTICATION_DISCOVERER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTHENTICATION_DISCOVERER_WIRING_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTO_DISABLE_CREDENTIAL_AFTER_AUTH_FAILURES_ENABLED_FLAG,
    type: 'BOOLEAN',
    defaultValue: false,
    supportedTargetTypes: ['application'],
  },
  {
    name: SHOULD_USE_CLAUDE_FOR_AUTH_DISCOVERY_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CREDENTIAL_STORES_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CREDENTIALS_STORE_FILESYSTEM_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTHENTICATION_COOLDOWN_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_PYTHON_TOOL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_ATTACK_LEARNINGS_ANALYZER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_ASK_CODE_BASE_WIDE_SEARCH_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: ASK_CODE_BASE_TOOL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AGGREGATE_HTTP_AUTH_COOKIES_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTHENTICATOR_HTTP_ONLY_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTH_PROBE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: PROBE_HTTP_CREDENTIAL_AUTO_APPLY_DISABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'ZIP_UPLOAD_INTEGRATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'BROWSER_IS_VIDEO_RECORDING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'BROWSER_TRACKING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'LONG_RUNNING_SMOKE_TEST_RESUME_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'IS_GLOBAL_INFERENCE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'CVE_MONITORING_SCHEDULER_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'CVE_EARLY_POC_FILTER_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'POC_GENERATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'POC_GATED_CVE_SIGNALS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'SMOKE_TEST_NEW_VIEW_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'UNIFIED_TRACES_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'AGENT_EXECUTIONS_V2_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'TRACE_INVESTIGATOR_VIEW_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'SIGNALS_CHAT_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ARCHIVED_ASSETS_LIFECYCLE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: UNVERIFIED_ASSETS_ALL_STATUSES_SHOWN_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ASSET_MERGE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ENDPOINT_HAR_REASSOCIATE,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SCHEDULED_ASSET_DEDUP_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: POST_BATCH_URL_PARAMETRIZATION_ASSET_DEDUP_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'WIZ_WEBHOOK_INGEST_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'XPANSE_INTEGRATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'XPANSE_ASSET_IMPORT_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ILI_PASSIVE_INTEL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CROWDSTRIKE_INTEGRATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'MANUAL_WEBHOOK_VERIFICATION_BYPASS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'MAX_HOURLY_SMOKE_TESTS_PER_APP',
    type: 'NUMBER' as const,
    defaultValue: 5,
  },
  {
    name: 'SMOKE_TEST_CREDENTIAL_VERIFICATION_TIMEOUT',
    type: 'NUMBER' as const,
    defaultValue: 480000,
  },
  {
    name: 'MAX_FILE_SIZE_MB',
    type: 'NUMBER' as const,
    defaultValue: 50,
  },
  {
    name: 'MAX_FILES_COUNT',
    type: 'NUMBER' as const,
    defaultValue: 10,
  },
  {
    name: 'GATEWAY_THROTTLE_TTL',
    type: 'NUMBER' as const,
    defaultValue: 60000,
  },
  {
    name: 'GATEWAY_THROTTLE_LIMIT',
    type: 'NUMBER' as const,
    defaultValue: 1000,
  },
  {
    name: 'GLOBAL_DAILY_AI_COST_CAP_USD',
    type: 'NUMBER' as const,
    defaultValue: 5000,
    supportedTargetTypes: [] as const,
  },
  {
    name: 'APPLICATION_DAILY_AI_COST_CAP_USD',
    type: 'NUMBER' as const,
    defaultValue: 1000,
    supportedTargetTypes: [] as const,
  },
  {
    name: 'CODE_ANALYSIS_SIGNALS_REQUIRE_ENDPOINT',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: CODE_EXECUTION_KUBERNETES_DRIVER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SCAN_JOB_KUBERNETES_DRIVER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'USER_TENANTS_CACHE_TTL',
    type: 'NUMBER' as const,
    defaultValue: 300,
  },
  {
    name: 'TENANT_HIERARCHY_CACHE_TTL',
    type: 'NUMBER' as const,
    defaultValue: 3600,
  },
  {
    name: BEDROCK_SDK_MAX_ATTEMPTS_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_BEDROCK_SDK_MAX_ATTEMPTS,
  },
  {
    name: 'HTTP_TOOL_TIMEOUT_MS',
    type: 'NUMBER' as const,
    defaultValue: 125000,
  },
  {
    name: 'INTERACTSH_BACKGROUND_TIMEOUT_MS',
    type: 'NUMBER' as const,
    defaultValue: 1800000,
  },
  {
    name: 'INTERACTSH_FORCE_KILL_TIMEOUT_MS',
    type: 'NUMBER' as const,
    defaultValue: 30000,
  },
  {
    name: 'GUARDRAILS_AND_TESTING_INSTRUCTIONS_SANITIZATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'KNOWLEDGE_SYNC_CONFLUENCE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'KNOWLEDGE_SYNC_NOTION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'KNOWLEDGE_SYNC_GOOGLE_DRIVE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'KNOWLEDGE_SYNC_BOX_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'SIEM_SPLUNK_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'SIEM_CROWDSTRIKE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'SIEM_XSIAM_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PREVENTION_WAF_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PREVENTION_NETWORK_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PREVENTION_CONFIG_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PREVENTION_IPS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PREVENTION_KILL_SWITCH',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CLOUD_AWS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CLOUD_GCP_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CLOUD_AZURE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'EXTERNAL_NETWORK_CLOUD_INVENTORY_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'EXTERNAL_NETWORK_CLOUD_FRONTING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'EXTERNAL_NETWORK_CLOUD_STORAGE_ASSETS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'EXTERNAL_NETWORK_DNS_ZONE_SYNC_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'NATIVE_PAGERDUTY_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'NATIVE_GITHUB_ISSUES_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: JIRA_CONFIGURATION_PROFILES_UI_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'OPS_SLACK_NOTIFICATIONS_DISABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'FEATURE_ANALYSIS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: FEATURE_ANALYSIS_CREDENTIAL_REMOVAL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'FEATURE_ANALYSIS_FILE_UPLOAD_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'HYBRID_NAV_DISCOVERY_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
] as const satisfies readonly FeatureFlagDefinitionEntryInput[];
